import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { useUserDetailsApi } from '../hooks/useUserDetails';

interface AccountActionContextType {
    fetchAgencyUsers: (agencyId: string) => Promise<any>;
    sendUserInvitation: (inviteData: { email: string; agencyId: string; role: string; senderEmail: string }) => Promise<any>;
    fetchPendingAgencyInvites: (agencyId: string) => Promise<any>;
    cancelUserInvite: (inviteId: string) => Promise<any>;
    removeAgencyUsers: (agencyId: string, userIds: string[], userId: string) => Promise<any>;
    deleteAgencyUser: (userId: string) => Promise<any>;
}

const AccountActionContext = createContext<AccountActionContextType | undefined>(undefined);

interface AccountActionProps {
    children: ReactNode;
}

export const AccountActionProvider: React.FC<AccountActionProps> = ({
    children,
}) => {
    const { 
        fetchAgencyUsers, 
        sendUserInvitation, 
        fetchPendingAgencyInvites, 
        cancelUserInvite, 
        removeAgencyUsers, 
        deleteAgencyUser 
    } = useUserDetailsApi();

    const contextValue: AccountActionContextType = useMemo(() => ({
        fetchAgencyUsers,
        sendUserInvitation,
        fetchPendingAgencyInvites,
        cancelUserInvite,
        removeAgencyUsers,
        deleteAgencyUser
    }), [fetchAgencyUsers, sendUserInvitation, fetchPendingAgencyInvites, cancelUserInvite, removeAgencyUsers, deleteAgencyUser]);

    return (
        <AccountActionContext.Provider value={contextValue}>
            {children}
        </AccountActionContext.Provider>
    );
};

export const useAccountActionContext = () => {
    const context = useContext(AccountActionContext);
    if (!context) {
        throw new Error('useAccountActionContext must be used within an AccountActionProvider');
    }
    return context;
};
